<template>
  <a-card :bordered="false" class="bank-panel">
    <template #title>
      <div class="transactions__header">
        <div>
          <span>Transactions</span>
          <p class="transactions__subtitle" v-if="account">
            {{ account.account_name || account.name }} · last 90 days
          </p>
        </div>
        <a-tag color="cyan">{{ transactions.length }} loaded</a-tag>
      </div>
    </template>

    <div v-if="loading" class="transactions__state">
      <a-spin />
    </div>

    <a-alert v-else-if="error" type="error" show-icon :message="error" />

    <a-empty v-else-if="!transactions.length" description="Select an account to view stored transactions." />

    <div v-else class="transactions__list">
      <div v-for="transaction in transactions" :key="transaction.id" class="transactions__item">
        <div class="transactions__details">
          <div class="transactions__topline">
            <h3>{{ transaction.description || transaction.name || transaction.merchant_name || 'Transaction' }}</h3>
            <div class="transactions__tags">
              <a-tag v-if="transaction.merchant_name">{{ transaction.merchant_name }}</a-tag>
              <a-tag v-if="transaction.status" color="blue">{{ transaction.status }}</a-tag>
            </div>
          </div>
          <p>
            {{ formatDate(transaction.booking_date || transaction.transaction_date) }}
          </p>
        </div>
        <div class="transactions__amount" :class="{ 'transactions__amount--debit': isDebit(transaction.amount) }">
          {{ formatMoney(transaction.amount, transaction.currency || account?.currency) }}
        </div>
      </div>
    </div>
  </a-card>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'Transactions',
  props: {
    account: {
      type: Object,
      default: null,
    },
    transactions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  setup() {
    const isDebit = (value) => Number(value || 0) < 0;

    const formatMoney = (value, currency) => {
      const amount = Number(value || 0);

      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: currency || 'GBP',
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currency || 'GBP'}`;
      }
    };

    const formatDate = (value) => {
      if (!value) {
        return 'Unknown date';
      }

      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(new Date(value));
    };

    return {
      formatMoney,
      formatDate,
      isDebit,
    };
  },
});
</script>

<style scoped>
.bank-panel {
  border: 1px solid #d8e2ec;
  border-radius: 22px;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.bank-panel :deep(.ant-card-head) {
  border-bottom: 1px solid #eef3f8;
}

.transactions__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.transactions__subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.transactions__state {
  min-height: 220px;
  display: grid;
  place-items: center;
}

.transactions__list {
  display: grid;
  gap: 12px;
}

.transactions__item {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.transactions__details {
  display: grid;
  gap: 8px;
}

.transactions__topline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.transactions__tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.transactions__item h3 {
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 17px;
}

.transactions__item p {
  margin: 0;
  color: #64748b;
}

.transactions__amount {
  align-self: center;
  font-weight: 700;
  white-space: nowrap;
  color: #0f766e;
  font-size: 18px;
}

.transactions__amount--debit {
  color: #b91c1c;
}

@media only screen and (max-width: 767px) {
  .transactions__header,
  .transactions__topline {
    flex-direction: column;
    align-items: flex-start;
  }

  .transactions__tags {
    justify-content: flex-start;
  }

  .transactions__item {
    flex-direction: column;
  }
}
</style>